import { CSSProperties, PropsWithChildren, useCallback, useEffect, useRef, useState } from 'react'
import debounce from '@/utils/debounce/debounce.ts'
import cn from '@/utils/cn.ts'
import useForceUpdate from '@/shared/hooks/useForceUpdate.ts'

interface IProps {
  initialWidth: number
  resizerClassname: string
  wrapperClassname?: string
  wrapperStyle?: CSSProperties
}

const ResizableByDrag = (props: PropsWithChildren<IProps>) => {
  const forceUpdate = useForceUpdate()

  const [width, setWidth] = useState(props.initialWidth) // State to hold the current width of the resizable box, initialized to 200px
  const boxRef = useRef<HTMLDivElement>(null) // Reference to the DOM element of the resizable box
  const isResizing = useRef(false) // Mutable ref to track whether the user is currently resizing the box
  const initialX = useRef(0) // Ref to store the initial X position of the mouse when resizing starts
  const initialWidth = useRef(0) // Ref to store the initial width of the box when resizing starts

  const handleMouseDown = (e) => {
    e.stopPropagation()
    e.preventDefault()

    isResizing.current = true // Set resizing state to true
    initialX.current = e.clientX // Store the X coordinate of the mouse when the user starts resizing
    initialWidth.current = boxRef?.current?.offsetWidth // Store the current width of the box
    document.addEventListener('mousemove', handleMouseMove) // Attach mousemove event listener to track mouse movement during resize
    document.addEventListener('mouseup', handleMouseUp) // Attach mouseup event listener to end resizing when mouse button is released
  }

  // `useCallback` to memoize the `handleMouseMove` function to prevent it from being recreated on every render
  const handleMouseMove = useCallback(
    debounce((e) => {
      e.stopPropagation()
      e.preventDefault()

      if (isResizing.current) {
        // Check if resizing is active
        const newWidth = initialWidth.current + (e.clientX - initialX.current) // Calculate new width based on mouse movement

        if (newWidth > 100) {
          // Check if the new width is greater than the minimum allowed width
          setWidth(newWidth) // Update the width state
        }
      }
    }, 10), // Debounce delay to limit the rate at which `handleMouseMove` is called (every 10ms)
    [] // Dependencies array is empty, so this callback will not be recreated unless its dependencies change
  )

  const handleMouseUp = () => {
    isResizing.current = false // Set resizing state to false

    // Remove the event listeners when done to prevent memory leaks and unnecessary event handling
    document.removeEventListener('mousemove', handleMouseMove)
    document.removeEventListener('mouseup', handleMouseUp)

    forceUpdate() // force update to remove hover effect
  }

  // Cleanup effect to ensure that event listeners are removed if the component unmounts during a resize operation
  useEffect(() => {
    return () => {
      // Remove event listeners to prevent memory leaks
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }
  }, [handleMouseMove]) // `handleMouseMove` is a dependency to ensure cleanup occurs properly if the callback changes

  const normalizedWrapperStyle = props.wrapperStyle || {}
  return (
    <div
      className={cn('inline-flex-gap gap8', props.wrapperClassname)}
      ref={boxRef} // Attach ref to the box element to manipulate the DOM directly
      style={{ ...normalizedWrapperStyle, width: `${width}px` }} // Inline style to set the width of the box
    >
      {props.children} {/* Render any children passed to this component */}
      <div
        data-active={isResizing.current}
        className={props.resizerClassname} // This is the handle the user interacts with to resize the box
        onMouseDown={handleMouseDown} // Attach `onMouseDown` event to initiate resizing
      />
    </div>
  )
}

export default ResizableByDrag
